import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/components/useConfirm";
import { Plus, Trash2, Pencil, BadgeCheck } from "lucide-react";

const STATUS_STYLES = {
  EXPIRED: { label: "Expired", cls: "bg-red-100 text-red-700 border-red-200" },
  URGENT: { label: "≤30 days", cls: "bg-red-100 text-red-700 border-red-200" },
  WARNING: { label: "≤60 days", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  WATCH: { label: "≤90 days", cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  OK: { label: "OK", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  UNKNOWN: { label: "—", cls: "bg-gray-100 text-gray-600 border-gray-200" },
};

const empty = {
  user_id: "", visa_type: "", visa_number: "", country_of_issue: "",
  start_date: "", expiry_date: "", work_hour_restrictions: "", notes: "",
  is_active: true,
};

function VisaDialog({ open, onOpenChange, initial, employees, onSaved }) {
  const editing = !!(initial && initial.id);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      if (editing) {
        setForm({
          user_id: initial.user_id || "",
          visa_type: initial.visa_type || "",
          visa_number: initial.visa_number || "",
          country_of_issue: initial.country_of_issue || "",
          start_date: (initial.start_date || "").slice(0, 10),
          expiry_date: (initial.expiry_date || "").slice(0, 10),
          work_hour_restrictions: initial.work_hour_restrictions || "",
          notes: initial.notes || "",
          is_active: initial.is_active !== false,
        });
      } else {
        setForm(empty);
      }
    }
  }, [open, editing, initial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.user_id) { toast.error("Please select an employee"); return; }
    if (!form.visa_type) { toast.error("Visa type is required"); return; }
    setBusy(true);
    try {
      const body = { ...form };
      Object.keys(body).forEach((k) => { if (body[k] === "") body[k] = null; });
      if (editing) {
        await api.patch(`/admin/visas/${initial.id}`, body);
        toast.success("Visa updated");
      } else {
        await api.post("/admin/visas", body);
        toast.success("Visa added");
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit visa record" : "Add visa record"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Employee</Label>
            <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })} disabled={editing}>
              <SelectTrigger data-testid="visa-form-user"><SelectValue placeholder="Choose employee" /></SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.employee_code} · {e.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Visa type</Label>
              <Input data-testid="visa-form-type" value={form.visa_type} onChange={(e) => setForm({ ...form, visa_type: e.target.value })} placeholder="Working Holiday, Resident…" />
            </div>
            <div>
              <Label>Visa number</Label>
              <Input data-testid="visa-form-number" value={form.visa_number} onChange={(e) => setForm({ ...form, visa_number: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Country of issue</Label>
            <Input data-testid="visa-form-country" value={form.country_of_issue} onChange={(e) => setForm({ ...form, country_of_issue: e.target.value })} placeholder="United Kingdom" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Start date</Label>
              <Input data-testid="visa-form-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <Label>Expiry date</Label>
              <Input data-testid="visa-form-expiry" type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Work hour restrictions</Label>
            <Input data-testid="visa-form-restrict" value={form.work_hour_restrictions} onChange={(e) => setForm({ ...form, work_hour_restrictions: e.target.value })} placeholder="e.g. Max 30 hrs/week" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea data-testid="visa-form-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="visa-form-submit" disabled={busy} className="bg-[#1A2744] hover:bg-[#0F1A30]">
              {busy ? "Saving…" : editing ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminVisas() {
  const [visas, setVisas] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, uRes] = await Promise.all([
        api.get("/admin/visas"),
        api.get("/admin/users", { params: { role: "EMPLOYEE" } }),
      ]);
      setVisas(vRes.data.visas || []);
      setEmployees(uRes.data.users || []);
    } catch {
      toast.error("Failed to load visas");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (v) => {
    const ok = await confirm({
      title: "Delete visa record?",
      description: `Permanently remove visa "${v.visa_type}" for ${v.employee_code} · ${v.user_name}.`,
      confirmText: "Delete",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/visas/${v.id}`);
      toast.success("Visa deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = filterStatus === "ALL" ? visas : visas.filter((v) => v.status === filterStatus);

  const counts = visas.reduce(
    (acc, v) => {
      acc[v.status] = (acc[v.status] || 0) + 1;
      return acc;
    },
    {},
  );

  return (
    <div className="space-y-8" data-testid="admin-visas-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#1A2744]">Visa tracker</h1>
          <p className="text-sm text-gray-500 mt-1">Live status with 30 / 60 / 90-day rolling traffic-light alerts.</p>
        </div>
        <Button data-testid="visas-add-btn" onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#1A2744] hover:bg-[#0F1A30]">
          <Plus className="h-4 w-4 mr-2" /> Add visa
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {["EXPIRED", "URGENT", "WARNING", "WATCH", "OK"].map((s) => (
          <Card key={s} className="border-gray-200">
            <CardContent className="p-4">
              <div className="text-xs uppercase tracking-wide text-gray-500">{STATUS_STYLES[s].label}</div>
              <div className="text-2xl font-bold text-[#1A2744]" data-testid={`visa-stat-${s}`}>{counts[s] || 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-heading text-lg font-bold text-[#1A2744]">All visa records</h3>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-44" data-testid="visa-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                {Object.keys(STATUS_STYLES).map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_STYLES[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Visa</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Expiry</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Loading…</TableCell></TableRow>}
              {!loading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-gray-500">
                  <BadgeCheck className="h-8 w-8 mx-auto mb-2 opacity-40" /> No visa records {filterStatus === "ALL" ? "yet" : `with status ${STATUS_STYLES[filterStatus]?.label}`}.
                </TableCell></TableRow>
              )}
              {filtered.map((v) => (
                <TableRow key={v.id} data-testid={`visa-row-${v.id}`}>
                  <TableCell>
                    <div className="font-medium text-[#1A2744]">{v.employee_code}</div>
                    <div className="text-xs text-gray-500">{v.user_name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-[#1A2744]">{v.visa_type}</div>
                    <div className="text-xs text-gray-500">{v.visa_number || "—"}</div>
                    {v.work_hour_restrictions && (
                      <div className="text-xs text-amber-700 mt-1">⏱ {v.work_hour_restrictions}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{v.country_of_issue || "—"}</TableCell>
                  <TableCell className="text-sm">
                    <div>{v.expiry_date || "—"}</div>
                    {v.days_to_expiry !== null && (
                      <div className="text-xs text-gray-500">
                        {v.days_to_expiry < 0 ? `${-v.days_to_expiry}d ago` : `in ${v.days_to_expiry}d`}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`border ${STATUS_STYLES[v.status]?.cls || ""}`}>
                      {STATUS_STYLES[v.status]?.label || v.status}
                    </Badge>
                    {!v.is_active && <div className="text-[10px] text-gray-400 mt-1">archived</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`visa-edit-${v.id}`} size="sm" variant="outline" onClick={() => { setEditing(v); setOpen(true); }}>
                      <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                    </Button>
                    <Button data-testid={`visa-delete-${v.id}`} size="sm" variant="ghost" className="text-red-600 hover:text-red-700 ml-1" onClick={() => remove(v)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <VisaDialog open={open} onOpenChange={setOpen} initial={editing} employees={employees} onSaved={load} />
    </div>
  );
}
